import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button as BSButton } from 'reactstrap';
import { MotorplayContext } from '../../../context';
import { Loading, Modal, PlanCard } from './components';
import ItemsCarousel from 'react-items-carousel';
import { useResponsiveValue } from '@theme-ui/match-media';
import {
  useInjectBodyScripts,
  useInjectHeadScripts,
} from '../../../utils/hooks/useInjectScripts';
import { injectTrackingScripts } from './utils';
import FreePassForm from '../../FreePassFormContainer';
import MotorplaySwitch from '../../form/SwitchRecurrence';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { resetProductExchange } from '../../../queries/actions';
import { applyProductRules } from '../../../queries/actions/rules';
import { ProductType, RulesName, RulesType } from '../../../enums';
import { translate } from '../../Translate';

const ProductsPage = ({ history, user, addProduct, products, shoppingCart = {}, setCurrentSubPaymentMethod, cleanOrder, hiddenCouponInput, subPaymentMethods, queryParamsObject, isSingleSubscriptionClient, isAddOnsEnabled = true, isLoading: isSubPaymentMethodsLoading, client, userAddOnsPurchased = [] }) => {
  const {
    setPlanToBuy,
    selectedPlan,
    loading,
    error,
    complete,
    resetError,
    openModal,
    type,
    setOpenModal,
    createSubscriptionStripe,
    completeByCoupon,
    resetErrorCoupon,
    sucessByCoupon,
    setLoading,
  } = useContext(MotorplayContext);
  const [query, setQuery] = useState(null);
  const [isSubmitFreeCode, setIsSubmitFreeCode] = useState(false);
  const [freePassResponse, setFreePassResponse] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [expandedDescriptionCardId, setExpandedDescriptionCardId] = useState(null);
  const numberOfCards = useResponsiveValue([1,2,2]);
  const switchRecurrenceRef = useRef(false);
  const autoRedirectTriggeredRef = useRef(false);
  const lastPreselectedProductIdRef = useRef(null);
  const explicitProductId = queryParamsObject?.productID || queryParamsObject?.productId;
  const normalizedExplicitProductId =
    explicitProductId == null || explicitProductId === ''
      ? undefined
      : Array.isArray(explicitProductId)
        ? explicitProductId[0]
        : explicitProductId;
  const hasPromoSlugOnly = Boolean(queryParamsObject?.promoSlug && !explicitProductId);
  const shouldIgnoreHiddenProducts = Boolean(queryParamsObject?.promoSlug);
  const isProductAvailableForFlow = useCallback((product) => {
    return shouldIgnoreHiddenProducts || !product.hidden;
  }, [shouldIgnoreHiddenProducts]);
  const purchasedAddOnProductIds = useMemo(
    () => new Set((userAddOnsPurchased || []).map((subscription) => subscription.productId)),
    [userAddOnsPurchased]
  );
  const isAddOnNotPurchased = useCallback(
    (product) => !purchasedAddOnProductIds.has(product.productID),
    [purchasedAddOnProductIds]
  );
  const filterPlansForDisplay = useCallback(
    (productList) =>
      (productList || []).filter(
        (product) => product.productType === ProductType.PLAN && isProductAvailableForFlow(product)
      ),
    [isProductAvailableForFlow]
  );
  const filterAddOnsForDisplay = useCallback(
    (productList) =>
      (productList || []).filter(
        (product) =>
          product.productType === ProductType.ADD_ON &&
          isProductAvailableForFlow(product) &&
          isAddOnNotPurchased(product)
      ),
    [isAddOnNotPurchased, isProductAvailableForFlow]
  );
  const [switchRecurrence, setSwitchRecurrence] = useState(!products.some(plan => plan.interval.unit === 'month'));
  const [planOrAddonState, setPlanOrAddonState] = useState(() => {
    const plans = filterPlansForDisplay(products);
    if (plans.length) return plans;
    return isAddOnsEnabled ? filterAddOnsForDisplay(products) : [];
  });

  const intl = useIntl();

  useInjectHeadScripts();
  useInjectBodyScripts(true);

  const buildMethodsPath = useCallback((paymentMethod, type) => {
    return `/${client}/subscribe/methods/${paymentMethod}/${type}${history.location.search}`;
  }, [client, history.location.search]);

  const closeModal = () => {
    if (error) {
      resetError();
    }

    if (complete || completeByCoupon) {
      injectScripts();
      const cloudpayConfig = window.AppENV.cloudpay;
      window.location.href = `${
        cloudpayConfig.baseURL
      }/redirect/goBackToReturnUrl${
        window.location.search
      }&jwt=${localStorage.getItem('jwt')}`;
      return;
    }

    setOpenModal(false);
  };

  const goPayment = (e) => {
    e.preventDefault();
    resetErrorCoupon();
    cleanOrder();
    const selectedProductData = products.find((product) => product._id === selectedPlan);
    applyProductRules({ ruleType: RulesType.PURCHASE, ruleName: RulesName.PLAN_INCLUDE_ADDONS, productId: selectedProductData.productID });
    const addOnsTotalLength = isAddOnsEnabled ? filterAddOnsForDisplay(products).length : 0;

    if (isSingleSubscriptionClient || addOnsTotalLength === 0 || !selectedProductData.addOnTrigger) {
      addProduct({ ...selectedProductData });
      const currentSubPaymentMethod = subPaymentMethods.find(sub => sub.countryCode.toLowerCase() === query.country.toLowerCase() && sub.active);
      setCurrentSubPaymentMethod(currentSubPaymentMethod);
      
      if (selectedProductData.productType === ProductType.ADD_ON) {        
        hiddenCouponInput(true);
      } else {
        hiddenCouponInput(false);
      }
      history.push(buildMethodsPath(currentSubPaymentMethod.paymentMethod, currentSubPaymentMethod.type));
      
    } else {
      applyProductRules({ ruleType: RulesType.PURCHASE, ruleName: RulesName.PLAN_INCLUDE_ADDONS, productId: selectedProductData.productID });
      history.push(`/${client}/subscribe/subscriptions/addOns${history.location.search}`);
    }
  };

  const handleSwitchPlan = () => {
    if (!isAddOnsEnabled) return setPlanOrAddonState(filterPlansForDisplay(products));
    const updatedSwitchRecurrence = switchRecurrenceRef.current;
    if (!updatedSwitchRecurrence) {
      return setPlanOrAddonState(filterPlansForDisplay(products));
    }
    return setPlanOrAddonState(filterAddOnsForDisplay(products));
  };

  useEffect(() => {
    if (!products?.length) return;

    if (switchRecurrenceRef.current) {
      setPlanOrAddonState(filterAddOnsForDisplay(products));
      return;
    }

    const plans = filterPlansForDisplay(products);
    if (plans.length) {
      setPlanOrAddonState(plans);
      return;
    }

    if (isAddOnsEnabled) {
      setPlanOrAddonState(filterAddOnsForDisplay(products));
    }
  }, [filterAddOnsForDisplay, filterPlansForDisplay, isAddOnsEnabled, products, purchasedAddOnProductIds]);

  const injectScripts = useCallback(() => {
    injectTrackingScripts(selectedPlan, products);
  }, [selectedPlan, products]);

  useEffect(() => {
    if (freePassResponse) {
      sucessByCoupon();
      setOpenModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freePassResponse]);
  
  useEffect(() => {
    if (isSubmitFreeCode) {
      setLoading();
      return;
    }

    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitFreeCode]);

  useEffect(() => {
    if (!query) return;
    if (!query.payment_id || query.country.toLowerCase() === 'ar') return;

    createSubscriptionStripe(user.token, query.payment_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (query) return;

    const params = new URLSearchParams(history.location.search);
    const paramsObject = Object.fromEntries(params.entries());

    setQuery(paramsObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.search]);

  const handleContinueClick = useCallback((product) => {
    resetProductExchange();
    cleanOrder();
    addProduct({ ...product });
    applyProductRules({ ruleType: RulesType.PURCHASE, ruleName: RulesName.PLAN_INCLUDE_ADDONS, productId: product.productID });
    const country = queryParamsObject?.country.toLowerCase() || query?.country.toLowerCase();
    const currentSubPaymentMethod = subPaymentMethods.find(sub => sub.countryCode.toLowerCase() === country && sub.active);
    setCurrentSubPaymentMethod(currentSubPaymentMethod);
    hiddenCouponInput(product.productType === ProductType.ADD_ON);
    history.push(buildMethodsPath(currentSubPaymentMethod.paymentMethod, currentSubPaymentMethod.type));
  }, [addProduct, buildMethodsPath, cleanOrder, hiddenCouponInput, history, query?.country, queryParamsObject?.country, setCurrentSubPaymentMethod, subPaymentMethods]);

  const hasSingleProductRedirectCandidate = Boolean(
    products?.length === 1 && (
      (explicitProductId && products[0]?.productID === explicitProductId) ||
      hasPromoSlugOnly
    )
  );
  const canAutoRedirect = hasSingleProductRedirectCandidate && subPaymentMethods?.length >= 1;

  useEffect(() => {
    if (!canAutoRedirect || autoRedirectTriggeredRef.current) return;
    autoRedirectTriggeredRef.current = true;
    handleContinueClick(products[0]);
  }, [canAutoRedirect, handleContinueClick, products]);

  const productsRef = useRef(products);
  const shoppingCartRef = useRef(shoppingCart);
  productsRef.current = products;
  shoppingCartRef.current = shoppingCart;

  const planOrAddonStateKey = useMemo(
    () => (planOrAddonState || []).map((p) => `${p._id}:${Boolean(p.active)}`).join('|'),
    [planOrAddonState]
  );

  useEffect(() => {
    const catalog = productsRef.current;
    if (!catalog?.length || catalog.length === 1) return;

    const carouselPlans = (planOrAddonState || []).filter((product) => product.active);
    if (!carouselPlans.length) return;

    if (!normalizedExplicitProductId) {
      lastPreselectedProductIdRef.current = null;
    }

    if (normalizedExplicitProductId) {
      if (lastPreselectedProductIdRef.current === normalizedExplicitProductId) {
        return;
      }
      const matchIdx = carouselPlans.findIndex(
        (p) => p.productID === normalizedExplicitProductId
      );
      if (matchIdx >= 0) {
        lastPreselectedProductIdRef.current = normalizedExplicitProductId;
        const chosen = carouselPlans[matchIdx];
        if (selectedPlan !== chosen._id) {
          setPlanToBuy(chosen._id);
        }
        setActiveItemIndex(matchIdx);
        const hasChosen = (shoppingCartRef.current?.productsToBuy || []).some((p) => p._id === chosen._id);
        if (!hasChosen) {
          const full = catalog.find((p) => p._id === chosen._id);
          if (full) {
            cleanOrder();
            addProduct({ ...full });
          }
        }
        return;
      }
    }

    const selectedStillValid =
      selectedPlan && carouselPlans.some((p) => p._id === selectedPlan);
    if (selectedStillValid) return;

    const chosen = carouselPlans[0];
    if (selectedPlan !== chosen._id) {
      setPlanToBuy(chosen._id);
    }
    setActiveItemIndex(0);
    const hasChosen = (shoppingCartRef.current?.productsToBuy || []).some((p) => p._id === chosen._id);
    if (!hasChosen) {
      const full = catalog.find((p) => p._id === chosen._id);
      if (full) {
        cleanOrder();
        addProduct({ ...full });
      }
    }
  }, [
    addProduct,
    cleanOrder,
    normalizedExplicitProductId,
    planOrAddonStateKey,
    products?.length,
    selectedPlan,
    setPlanToBuy,
  ]);

  const activeCarouselPlans = useMemo(
    () => (planOrAddonState || []).filter((product) => product.active),
    [planOrAddonState]
  );

  const safeActiveItemIndex = useMemo(() => {
    const n = activeCarouselPlans.length;
    if (n <= 0) return 0;
    return Math.min(Math.max(0, activeItemIndex), n - 1);
  }, [activeItemIndex, activeCarouselPlans]);

  useEffect(() => {
    const n = activeCarouselPlans.length;
    if (n === 0) return;
    if (activeItemIndex > n - 1) {
      setActiveItemIndex(n - 1);
    }
  }, [activeCarouselPlans, activeItemIndex]);

  useEffect(() => {
    setExpandedDescriptionCardId(null);
  }, [safeActiveItemIndex, planOrAddonStateKey]);

  if (hasSingleProductRedirectCandidate) {
    if (!canAutoRedirect || isSubPaymentMethodsLoading) {
      return <Loading />;
    }

    return null;
  }

  const filteredProducts = planOrAddonState.filter(isProductAvailableForFlow)
  if (filteredProducts.length === 0) {
    return (
      <>
        <Box
          sx={{ width: '100%', marginTop: '7rem' }}
          className='MembershipSelectionPage'
        >
          <Box className='myaccount-title'>
            {isAddOnsEnabled && (
              <div className="myaccount-title__swith-recurrence">
                <Typography component="h6">
                  <FormattedMessage id="MembershipSelectionPage.recurrence.monthly.card" />
                </Typography>
                <MotorplaySwitch checked={switchRecurrence} onChange={e => {
                  setSwitchRecurrence(!switchRecurrence);
                  switchRecurrenceRef.current = !switchRecurrence
                  handleSwitchPlan();
                }} />
                <Typography component="h6">
                  <FormattedMessage id="MembershipSelectionPage.recurrence.ppv" />
                </Typography>
              </div>
            )}
          </Box>
        </Box>
      <div
        className="MembershipSelectionPage__empty"
        style={{
          backgroundColor: '#0c1a2b',
          padding: '4rem 2rem',
          textAlign: 'center',
          borderRadius: '8px',
        }}
      >
        <div className="myaccount-title mx-5">
          <h3
            className="text-title"
            style={{ color: '#b0bdcc' }}
          >
            <FormattedMessage id="MembershipSelectionPage.no.products.title" defaultMessage={translate(intl, 'ProductSelectionPage.no.products.title')} />
          </h3>
          <h6 className="myaccount-title__short-description" style={{ color: '#8a99ad' }} >
            <FormattedMessage
              id="MembershipSelectionPage.no.products.description"
              defaultMessage={translate(intl, 'ProductSelectionPage.no.products.description')}
            />
          </h6>
        </div>
      </div>
      </>
    );
  }
  
  return (
    <>
      {loading && <Loading />}
      {openModal && type && (
        <Modal type={type} closeModal={closeModal} message={error} />
      )}
      <Box
        sx={{ width: '100%', marginTop: '7rem' }}
        className='MembershipSelectionPage'
      >
        <Box className='myaccount-title'>
          {isAddOnsEnabled && (
            <div className="myaccount-title__swith-recurrence">
              <Typography component="h6">
                <FormattedMessage id="MembershipSelectionPage.recurrence.monthly.card" />
              </Typography>
              <MotorplaySwitch checked={switchRecurrence} onChange={e => {
                setSwitchRecurrence(!switchRecurrence);
                switchRecurrenceRef.current = !switchRecurrence
                handleSwitchPlan();
              }} />
              <Typography component="h6">
                <FormattedMessage id="MembershipSelectionPage.recurrence.ppv" />
              </Typography>
            </div>
          )}
        </Box>
        <Box className='selectAddOnCarousel' style={{ width: (products?.length === 1 && numberOfCards !== 1) ? '30%' : '100%', margin: '0 auto' }}>
          <ItemsCarousel
            slidesToScroll={1}
            infiniteLoop={false}
            gutter={10}
            activePosition="center"
            disableSwipe={false}
            alwaysShowChevrons={false}
            numberOfCards={products?.length > 1 ? numberOfCards : 1}
            showSlither={false}
            activeItemIndex={safeActiveItemIndex}
            requestToChangeActive={(value) => setActiveItemIndex(value)}
            rightChevron={
              <div className="chevron chevron--right">
                <div className="chevron__icon text-white">
                  <FaChevronRight size={50} />
                </div>
              </div>
            }
            leftChevron={
              <div className="chevron chevron--left">
                <div className="chevron__icon text-white">
                  <FaChevronLeft size={50} />
                </div>
              </div>
            }
          >
            {products &&
              activeCarouselPlans.map((product) => (
                <PlanCard
                  plan={product}
                  key={product._id}
                  selectPlan={setPlanToBuy}
                  selected={selectedPlan === product._id}
                  addProduct={addProduct}
                  products={products}
                  cleanOrder={cleanOrder}
                  isDescriptionExpanded={expandedDescriptionCardId === product._id}
                  onDescriptionExpandChange={(expanded) => {
                    setExpandedDescriptionCardId(expanded ? product._id : null);
                  }}
                />
              ))}
          </ItemsCarousel>
        </Box>
        <Box sx={{ maxWidth: '25rem', margin: '0 auto', marginTop: '1rem' }}>
          <FreePassForm
            setIsSubmitFreeCode={setIsSubmitFreeCode}
            setFreePassResponse={setFreePassResponse}
          />
        </Box>
        <Box sx={{ maxWidth: '25rem', margin: '0 auto', marginTop: '2.5rem' }}>
          <BSButton
            className='button-continue'
            disabled={!selectedPlan}
            onClick={goPayment}
          >
            Continuar
          </BSButton>
        </Box>
      </Box>
    </>
  );
};

ProductsPage.defaultProps = {};

ProductsPage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
  }),
  user: PropTypes.object,
  client: PropTypes.string.isRequired,
  isAddOnsEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};
export default ProductsPage;
